import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import useNotifier from '@softwaredev_cegeka/stix-notifier/dist/hooks/useNotifier'
import ThemeContext from '@softwaredev_cegeka/stix-core/dist/components/ThemeProvider/ThemeContext'
import mapTranslations from 'src/utils/mfe/mapTranslations'
import { pushGtmDataLayer } from 'src/utils/gtm'
import {
    isAllAllowed,
    isAllowed,
    isAnyAllowed,
    isGlobalAdmin,
} from 'src/utils/permissions'
import { reverse } from 'src/routes/config'
import { createMfeLink, CROSS_MFE_LINKS } from 'src/routes/crossMfeLink'
import useUserData from 'src/hooks/useUserData'
import useSubsidiaries from 'src/hooks/useSubsidiaries'
import { PageHeaderContext } from 'src/components/PageHeader/pageHeaderContext'
import useMfe from 'src/hooks/useMfe'
import useMfeTranslations from 'src/hooks/useMfeTranslations'
import useTranslations from 'src/hooks/useTranslations'
import EventBus from 'src/utils/mfe/EventBus'
import mfeEvents from 'src/utils/mfe/mfeEvents'
import { createScopedResolve } from '/native/importMetaResolve.mjs'
import spawnWebWorker from 'src/utils/mfe/spawnWebWorker'
import { onNavigate } from 'src/routes/mfes/utils'
import useRuntimeConfig from 'src/hooks/useRuntimeConfig'

/**
 * @param {"mfe-example"|"mfe-hr-portal"|"mfe-erp-portal"|"mfe-itp"|"mfe-playbook"|"mfe-task-center"|"mfe-ccp2"|"mfe-csod"} mfeKey
 * @param {string} mfeBasePath path to host the MFE on.
 * @param {Node} rootNode DOM node to mount the app into.
 * @param {{[mfeConfig]: Object, [mfeUtilities]: Object}} [extraMountParams] extra properties passed to the `mount` call.
 */
export default function useMountedMfe(
    mfeKey,
    mfeBasePath,
    rootNode,
    extraMountParams = { mfeConfig: {}, mfeUtilities: {} }
) {
    const { mfe: mfeModule } = useMfe(mfeKey, true)
    const { i18n } = useTranslations('common', mfeKey)
    const { language } = i18n
    const prevLang = useRef(language)
    const translations = useMfeTranslations(mfeKey)('common', 'common')
    const [mountedModule, setMountedModule] = useState(null)
    const [error, setError] = useState(null)
    const history = useHistory()
    const userData = useUserData()
    const subsidiariesData = useSubsidiaries()
    const scopedResolve = useMemo(() => createScopedResolve(mfeKey), [mfeKey])
    const runtimeConfig = useRuntimeConfig()

    const {
        setTitle,
        setBreadcrumbs,
        pageHeaderActionsContainer,
        pageHeaderTitleExtras,
    } = useContext(PageHeaderContext)
    const notifier = useNotifier()
    const theme = useContext(ThemeContext)
    const mappedTranslations = useMemo(
        () => mapTranslations(translations),
        [translations]
    )

    const eventBusRef = useRef(null)

    useEffect(() => {
        const languageChanged = language !== prevLang.current
        prevLang.current = language
        if (!mfeModule || !rootNode || (mountedModule && !languageChanged)) {
            return
        }
        // We remount if the language changed. Make sure to also unmount.
        if (languageChanged && mfeModule.unmount) {
            mfeModule.unmount(rootNode)
        }
        eventBusRef.current = new EventBus()

        mfeModule
            .mount(rootNode, {
                history,
                ...extraMountParams,
                eventBus: eventBusRef.current,
                mfeConfig: {
                    ...(extraMountParams?.mfeConfig ?? {}),
                    userData,
                    subsidiariesData,
                    basePath: mfeBasePath,
                    translations: mappedTranslations,
                    language: language,
                    i18n: i18n,
                    theme: {
                        themeImages: theme?.activeTheme?.config,
                    },
                    initialLocation: history.location,
                    onNavigate: nextLocation => {
                        onNavigate({ nextLocation, history })
                    },
                },
                mfeUtilities: {
                    ...(extraMountParams?.mfeUtilities ?? {}),
                    resolveModule: scopedResolve,
                    spawnWebWorker: spawnWebWorker,
                    setTitle: setTitle,
                    setBreadcrumbs: setBreadcrumbs,
                    pushGtmDataLayer: pushGtmDataLayer,
                    pageHeaderActionsContainer: pageHeaderActionsContainer,
                    pageHeaderTitleExtras: pageHeaderTitleExtras,
                    getRuntimeConfig: (key = null) => {
                        return key ? runtimeConfig[key] : runtimeConfig
                    },
                    permissions: {
                        isAllAllowed: permissions => {
                            return isAllAllowed(userData, permissions)
                        },
                        isAllowed: permission => {
                            return isAllowed(userData, permission)
                        },
                        isAnyAllowed: permissions => {
                            return isAnyAllowed(userData, permissions)
                        },
                        isGlobalAdmin: () => {
                            return isGlobalAdmin(userData)
                        },
                        ...(extraMountParams?.mfeUtilities?.permissions ?? {}),
                    },
                    onError: e => {
                        console.error(e)
                        setError(e)
                    },
                    onNotFound: e => {
                        console.log('Not found: ', e)
                        history.push(reverse('not-found'))
                    },
                    onUnauthorized: () => {
                        history.push(reverse('unauthorized'))
                    },
                    createMfeLink,
                    CROSS_MFE_LINKS,
                    notifier,
                },
            })
            .then(m => {
                setMountedModule(m)
            })
    }, [
        mfeModule,
        history,
        mfeBasePath,
        translations,
        userData,
        subsidiariesData,
        mountedModule,
        setBreadcrumbs,
        setTitle,
        rootNode,
        extraMountParams,
        language,
        mappedTranslations,
        pageHeaderActionsContainer,
        pageHeaderTitleExtras,
        notifier,
        i18n,
        theme,
        scopedResolve,
        runtimeConfig,
    ])

    // Push action container updates.
    useEffect(() => {
        eventBusRef.current?.dispatch(
            mfeEvents['UPDATE:pageHeaderActionsContainer'],
            pageHeaderActionsContainer
        )
    }, [pageHeaderActionsContainer])

    // Push header extra updates.
    useEffect(() => {
        eventBusRef.current?.dispatch(
            mfeEvents['UPDATE:pageHeaderTitleExtras'],
            pageHeaderTitleExtras
        )
    }, [pageHeaderTitleExtras])

    // Update the MFE history if it supports history syncing.
    useEffect(() => {
        if (!mountedModule || !mountedModule?.updateHistory) {
            return
        }

        // This returns an unlisten function, allowing react to clean up.
        return history.listen(location => {
            mountedModule.updateHistory(location)
        })
    }, [mountedModule, history])

    // Unmount effect for triggering MFE unmount and cleaning up events.
    useEffect(() => {
        return () => {
            if (mfeModule?.unmount) {
                mfeModule.unmount(rootNode)
            }
            eventBusRef.current?.destroy()
            eventBusRef.current = null
        }
    }, [mfeModule, rootNode, mfeKey])

    // Unmount effect for resetting the title when we unmount the MFE
    useEffect(() => {
        return () => {
            // Clear the title when leaving a MFE.
            if (setTitle && typeof setTitle === 'function') {
                //This extra check is made to prevent the notifications mfe unmounting error in drawer mode
                setTitle('')
            }
        }
    }, [setTitle])

    if (error) {
        setError(null)
        throw error
    }

    return mountedModule
}
